/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agentApi } from '../../services/api';
import { AgentDetail, TemplateInfo } from '../../types/agent';
import { toast } from 'react-toastify';

interface AgentSyncButtonProps {
  agent: AgentDetail;
  variant?: 'button' | 'icon';
  size?: 'small' | 'medium' | 'large';
}

export const AgentSyncButton: React.FC<AgentSyncButtonProps> = ({
  agent,
  variant = 'button',
  size = 'medium',
}) => {
  const queryClient = useQueryClient();
  const templateInfo = agent.template_info;
  const isTemplate = agent.is_template === true;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Sync mutation for provisioned agents
  const syncMutation = useMutation({
    mutationFn: () => agentApi.syncFromTemplate(agent.id, { force: false }),
    onSuccess: (response) => {
      toast.success(response.message || 'Agent synced successfully');
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['agent', agent.id] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to sync agent');
    },
  });

  // Update mutation for template agents
  const updateProvisionedMutation = useMutation({
    mutationFn: () => agentApi.updateProvisionedAgents(agent.id, { force: true }),
    onSuccess: (response) => {
      const { updated_count = 0, created_count = 0, total_users = 0 } = response;
      
      let message = 'Provisioned agents updated successfully';
      if (updated_count > 0 || created_count > 0) {
        message = `Updated ${updated_count} agent(s) and created ${created_count} new agent(s) for ${total_users} total users`;
      }
      
      toast.success(message);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['agent', agent.id] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      setConfirmDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update provisioned agents');
      setConfirmDialogOpen(false);
    },
  });

  const handleUpdateClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmUpdate = () => {
    updateProvisionedMutation.mutate();
  };

  const handleCancelUpdate = () => {
    setConfirmDialogOpen(false);
  };

  // If this is a template agent, show "Update Provisioned Agents" button
  if (isTemplate) {
    if (variant === 'icon') {
      return (
        <>
            <span>
              <Button
                size={size}
                color="primary"
                onClick={handleUpdateClick}
                disabled={updateProvisionedMutation.isPending}
                sx={{ minWidth: 'auto', p: 1 }}
              >
                {updateProvisionedMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  <SyncIcon fontSize={size} />
                )}
              </Button>
            </span>
          
          <Dialog open={confirmDialogOpen} onClose={handleCancelUpdate}>
            <DialogTitle>Update Provisioned Agents</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will:
                <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                  <li>Force update all existing agents provisioned from this template</li>
                  <li>Create new agents for all users who don't have one yet</li>
                </ul>
                All users with agents based on this template will receive the latest changes,
                and users without this agent will get it provisioned automatically.
                This action cannot be undone. Continue?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelUpdate} disabled={updateProvisionedMutation.isPending}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmUpdate} 
                variant="contained" 
                color="primary"
                disabled={updateProvisionedMutation.isPending}
              >
                {updateProvisionedMutation.isPending ? 'Updating...' : 'Update All'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      );
    }

    return (
      <>
        <Button
          variant="contained"
          color="primary"
          size={size}
          startIcon={updateProvisionedMutation.isPending ? <CircularProgress size={20} /> : <SyncIcon />}
          onClick={handleUpdateClick}
          disabled={updateProvisionedMutation.isPending}
        >
          {updateProvisionedMutation.isPending ? 'Updating...' : 'Update Provisioned Agents'}
        </Button>
        
        <Dialog open={confirmDialogOpen} onClose={handleCancelUpdate}>
          <DialogTitle>Update Provisioned Agents</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will:
              <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                <li>Force update all existing agents provisioned from this template</li>
                <li>Create new agents for all users who don't have one yet</li>
              </ul>
              All users with agents based on this template will receive the latest changes,
              and users without this agent will get it provisioned automatically.
              This action cannot be undone. Continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelUpdate} disabled={updateProvisionedMutation.isPending}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmUpdate} 
              variant="contained" 
              color="primary"
              disabled={updateProvisionedMutation.isPending}
            >
              {updateProvisionedMutation.isPending ? 'Updating...' : 'Update All'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Don't show button if not template-based
  if (!templateInfo?.is_template_based) {
    return null;
  }

  // Don't show if already up to date
  if (!templateInfo.needs_sync) {
    return (
        <Box display="inline-flex" alignItems="center" gap={1}>
          <CheckCircleIcon color="success" fontSize={size} />
          {variant === 'button' && (
            <Typography variant="caption" color="success.main">
              Up to date
            </Typography>
          )}
        </Box>
    );
  }

  const handleSync = () => {
    syncMutation.mutate();
  };

  if (variant === 'icon') {
    return (
        <span>
          <Button
            size={size}
            color="warning"
            onClick={handleSync}
            disabled={syncMutation.isPending}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            {syncMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              <SyncIcon fontSize={size} />
            )}
          </Button>
        </span>
    );
  }

  return (
    <Button
      variant="outlined"
      color="warning"
      size={size}
      startIcon={syncMutation.isPending ? <CircularProgress size={20} /> : <SyncIcon />}
      onClick={handleSync}
      disabled={syncMutation.isPending}
    >
      {syncMutation.isPending ? 'Syncing...' : 'Sync Update'}
    </Button>
  );
};
